import { useCallback, useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useSearchActions, useSearchResults, useSearchState } from '#src/app/pages/search/shared/hooks';

type TrackData = { topic_value: string; context: 'landing_topic' | 'topic_filter' };
type Topic = {
	label: string;
	value: string;
	isRefined: boolean;
};

type Result = ModelHookResult<
	{
		topics: Topic[];
		disabled: boolean;
		isInitialLoading: boolean;
	},
	{
		setTopic: (value: string) => void;
		trackTopicsFilterClick: (data: TrackData) => void;
	}
>;

export function useTopicsFilter(): Result {
	const state = useSearchState();
	const results = useSearchResults();
	const actions = useSearchActions();
	const track = useTracking();

	const trackTopicsFilterClick = useCallback(
		(data: TrackData) => {
			void track('search', {
				...data,
				q: state.query || '',
			});
		},
		[state.query, track],
	);

	return {
		data: {
			isInitialLoading: results.isInitialLoad,
			topics: useMemo(() => {
				const facets = results.topicFacets.map((item) => ({
					label: item.label,
					value: item.value,
					isRefined: item.isRefined,
				}));
				// Selected topics are absent from facets when the query returns no results (the "No results"
				// screen). Surface them anyway so the selected chips still render and can be toggled off.
				const missingSelected = state.topics
					.filter((value) => !facets.some((facet) => facet.value === value))
					.map((value) => ({ label: value, value, isRefined: true }));
				return [...missingSelected, ...facets];
			}, [results.topicFacets, state.topics]),
			disabled: results.topicFacets.length === 0 && !results.isLoading,
		},
		actions: {
			setTopic: useCallback(
				(value: string) => {
					actions.toggleTopic(value);
				},
				[actions],
			),
			trackTopicsFilterClick,
		},
	};
}
